const TrialBalanceTable = ({ data = [], totalDebits = 0, totalCredits = 0 }) => {
  const formatPrice = (price) => {
    const absPrice = Math.abs(price || 0);
    return new Intl.NumberFormat("id-ID", {
      style: "decimal",
      minimumFractionDigits: 2,
    }).format(absPrice);
  };
  
  // Group accounts by main category (1-5)
  // Note: Need to handle different COA formats (e.g., 1.0.00 vs 1.01)
  const grouped = {
    "Aktiva Lancar": data.filter((acc) => {
      const code = acc.account_code;
      // Match both formats: 1.01.xx or 1.0.xx (old format)
      return code === "1" || 
             code === "1.0" || code === "1.00" || code === "1.0.00" ||
             code === "1.01" || code === "1.1" || code === "1.1.00" ||
             code.startsWith("1.0.") || 
             code.startsWith("1.01.") ||
             code.startsWith("1.1.");
    }),
    "Aktiva Tetap": data.filter((acc) => {
      const code = acc.account_code;
      // Match both formats: 1.02.xx or 1.2.xx (old format)
      return code === "1.02" || code === "1.2" || code === "1.2.00" ||
             code.startsWith("1.02.") ||
             code.startsWith("1.2.");
    }),
    "Hutang": data.filter((acc) => acc.account_code.startsWith("2")),
    "Modal": data.filter((acc) => acc.account_code.startsWith("3")),
    "Pendapatan": data.filter((acc) => acc.account_code.startsWith("4")),
    "Biaya": data.filter((acc) => acc.account_code.startsWith("5")),
    "Pajak": data.filter((acc) => acc.account_code === "5.04" || acc.account_code.startsWith("5.04.")),
  };

  // Calculate totals per group
  const totalPerGroup = (items) => {
    const opening = items.reduce((sum, i) => sum + (i.opening_balance || 0), 0);
    const mutationDebit = items.reduce((sum, i) => sum + (i.mutation_debit || 0), 0);
    const mutationCredit = items.reduce((sum, i) => sum + (i.mutation_credit || 0), 0);
    const closing = items.reduce((sum, i) => sum + (i.closing_balance || 0), 0);
    return { opening, mutationDebit, mutationCredit, closing };
  };

  const totalAktivaLancar = totalPerGroup(grouped["Aktiva Lancar"]);
  const totalAktivaTetap = totalPerGroup(grouped["Aktiva Tetap"]);
  const totalAktiva = {
    opening: totalAktivaLancar.opening + totalAktivaTetap.opening,
    mutationDebit: totalAktivaLancar.mutationDebit + totalAktivaTetap.mutationDebit,
    mutationCredit: totalAktivaLancar.mutationCredit + totalAktivaTetap.mutationCredit,
    closing: totalAktivaLancar.closing + totalAktivaTetap.closing,
  };
  
  const totalHutang = totalPerGroup(grouped.Hutang);
  const totalModal = totalPerGroup(grouped.Modal);
  const totalPassiva = {
    opening: totalHutang.opening + totalModal.opening,
    mutationDebit: totalHutang.mutationDebit + totalModal.mutationDebit,
    mutationCredit: totalHutang.mutationCredit + totalModal.mutationCredit,
    closing: totalHutang.closing + totalModal.closing,
  };
  
  const totalPendapatan = totalPerGroup(grouped.Pendapatan);
  
  // Filter out Pajak (5.04.xx) from Beban for separate calculation
  const bebanWithoutPajak = grouped.Biaya.filter(acc => !acc.account_code.startsWith("5.04"));
  const totalBeban = totalPerGroup(bebanWithoutPajak);
  const totalPajak = totalPerGroup(grouped.Pajak);
  
  // Calculate Laba Rugi
  // Note: For Revenue (4.x), closing_balance should be negative (credit balance)
  // For Expense (5.x), closing_balance should be positive (debit balance)
  // So: Net Income = |Revenue| - |Expense|
  const labaRugiSebelumPajak = Math.abs(totalPendapatan.closing) - Math.abs(totalBeban.closing);
  const labaRugiSetelahPajak = labaRugiSebelumPajak - Math.abs(totalPajak.closing);

  // Calculate GRAND TOTAL from backend API response (this is the source of truth)
  // Backend already calculated correctly: total_debits and total_credits
  const isBackendBalanced = Math.abs(totalDebits - totalCredits) < 0.01;

  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full border border-collapse text-sm">
        <thead className="bg-blue-900 text-white">
          <tr>
            <th className="p-2 border text-center" colSpan="2" rowSpan="2">Perkiraan</th>
            <th className="p-2 border text-center" rowSpan="2">Saldo Awal</th>
            <th className="p-2 border text-center" colSpan="2">Mutasi</th>
            <th className="p-2 border text-center" rowSpan="2">Saldo Akhir</th>
          </tr>
          <tr>
            <th className="p-2 border text-center">Debet</th>
            <th className="p-2 border text-center">Kredit</th>
          </tr>
        </thead>
        <thead className="bg-blue-800 text-white">
          <tr>
            <th className="p-2 border text-center w-[12%]">Kode</th>
            <th className="p-2 border text-left">Perkiraan</th>
            <th className="p-2 border text-center w-[15%]">Rp</th>
            <th className="p-2 border text-center w-[15%]">Rp</th>
            <th className="p-2 border text-center w-[15%]">Rp</th>
            <th className="p-2 border text-center w-[15%]">Rp</th>
          </tr>
        </thead>

        <tbody>
          {/* AKTIVA LANCAR */}
          <tr className="bg-gradient-to-r from-blue-50 to-blue-100">
            <td colSpan="6" className="p-2 border font-semibold">Aktiva Lancar</td>
          </tr>
          {grouped["Aktiva Lancar"].map((acc, idx) => (
            <tr key={idx} className="hover:bg-blue-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-blue-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Aktiva Lancar</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaLancar.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* AKTIVA TETAP */}
          <tr className="bg-gradient-to-r from-blue-50 to-blue-100">
            <td colSpan="6" className="p-2 border font-semibold">Aktiva Tetap</td>
          </tr>
          {grouped["Aktiva Tetap"].map((acc, idx) => (
            <tr key={idx} className="hover:bg-blue-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-blue-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Aktiva Tetap</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktivaTetap.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* TOTAL AKTIVA */}
          <tr className="bg-blue-300 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Aktiva</td>
            <td className="p-2 border text-right">{formatPrice(totalAktiva.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktiva.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktiva.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalAktiva.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>
          <tr><td colSpan="6" className="border-0 p-0"></td></tr>

          {/* HUTANG */}
          <tr className="bg-gradient-to-r from-yellow-50 to-yellow-100">
            <td colSpan="6" className="p-2 border font-semibold">Hutang</td>
          </tr>
          {grouped.Hutang.map((acc, idx) => (
            <tr key={idx} className="hover:bg-yellow-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-yellow-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Hutang</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalHutang.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* MODAL */}
          <tr className="bg-gradient-to-r from-purple-50 to-purple-100">
            <td colSpan="6" className="p-2 border font-semibold">Modal</td>
          </tr>
          {grouped.Modal.map((acc, idx) => (
            <tr key={idx} className="hover:bg-purple-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-purple-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Modal</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalModal.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* TOTAL PASSIVA */}
          <tr className="bg-purple-300 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Pasiva</td>
            <td className="p-2 border text-right">{formatPrice(totalPassiva.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPassiva.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPassiva.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPassiva.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>
          <tr><td colSpan="6" className="border-0 p-0"></td></tr>

          {/* PENDAPATAN */}
          <tr className="bg-gradient-to-r from-green-50 to-green-100">
            <td colSpan="6" className="p-2 border font-semibold">Pendapatan</td>
          </tr>
          {grouped.Pendapatan.map((acc, idx) => (
            <tr key={idx} className="hover:bg-green-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-green-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Pendapatan</td>
            <td className="p-2 border text-right">{formatPrice(totalPendapatan.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPendapatan.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPendapatan.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPendapatan.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* BIAYA (excluding Pajak) */}
          <tr className="bg-gradient-to-r from-red-50 to-red-100">
            <td colSpan="6" className="p-2 border font-semibold">Biaya</td>
          </tr>
          {bebanWithoutPajak.map((acc, idx) => (
            <tr key={idx} className="hover:bg-red-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-red-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Biaya</td>
            <td className="p-2 border text-right">{formatPrice(totalBeban.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalBeban.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalBeban.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalBeban.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* TOTAL LABA RUGI SEBELUM PAJAK */}
          <tr style={{ backgroundColor: labaRugiSebelumPajak >= 0 ? '#B2FFC5' : '#FFB2B2' }} className="font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Laba Rugi Sebelum Pajak</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSebelumPajak >= 0 ? labaRugiSebelumPajak : 0)}</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSebelumPajak < 0 ? -labaRugiSebelumPajak : 0)}</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSebelumPajak)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* PAJAK */}
          <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
            <td colSpan="6" className="p-2 border font-semibold">Pajak</td>
          </tr>
          {grouped.Pajak.map((acc, idx) => (
            <tr key={idx} className="hover:bg-gray-50 transition-colors">
              <td className="p-2 border font-mono text-sm">{acc.account_code}</td>
              <td className="p-2 border">{acc.account_name}</td>
              <td className="p-2 border text-right">{formatPrice(acc.opening_balance || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_debit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.mutation_credit || 0)}</td>
              <td className="p-2 border text-right">{formatPrice(acc.closing_balance || 0)}</td>
            </tr>
          ))}
          <tr className="bg-gray-200 font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Pajak</td>
            <td className="p-2 border text-right">{formatPrice(totalPajak.opening)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPajak.mutationDebit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPajak.mutationCredit)}</td>
            <td className="p-2 border text-right">{formatPrice(totalPajak.closing)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-1"></td></tr>

          {/* TOTAL LABA RUGI */}
          <tr style={{ backgroundColor: labaRugiSetelahPajak >= 0 ? '#B2FFC5' : '#FFB2B2' }} className="font-bold">
            <td colSpan="2" className="p-2 border text-right">Total Laba Rugi</td>
            <td className="p-2 border text-right">0.00</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSetelahPajak >= 0 ? labaRugiSetelahPajak : 0)}</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSetelahPajak < 0 ? -labaRugiSetelahPajak : 0)}</td>
            <td className="p-2 border text-right">{formatPrice(labaRugiSetelahPajak)}</td>
          </tr>
          <tr><td colSpan="6" className="border-0 p-2"></td></tr>

          {/* TOTAL PASIVA + LABA RUGI (Should equal Total Aktiva) */}
          <tr className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold text-base">
            <td colSpan="2" className="p-3 border text-right">TOTAL PASIVA + LABA RUGI</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.opening)}</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.mutationDebit + (labaRugiSetelahPajak >= 0 ? labaRugiSetelahPajak : 0))}</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.mutationCredit + (labaRugiSetelahPajak < 0 ? -labaRugiSetelahPajak : 0))}</td>
            <td className="p-3 border text-right">{formatPrice(totalPassiva.closing + labaRugiSetelahPajak)}</td>
          </tr>

          {/* Balance Check: Aktiva vs Pasiva+Laba */}
          {Math.abs(totalAktiva.closing - (totalPassiva.closing + labaRugiSetelahPajak)) < 0.01 ? (
            <tr className="bg-green-100">
              <td colSpan="6" className="p-3 border text-center text-green-700 font-bold">
                ✓ Balanced: Total Aktiva = Total Pasiva + Laba Rugi
                <br />
                <span className="text-sm">
                  Aktiva: {formatPrice(totalAktiva.closing)} | Pasiva + Laba: {formatPrice(totalPassiva.closing + labaRugiSetelahPajak)}
                </span>
              </td>
            </tr>
          ) : (
            <tr className="bg-red-100">
              <td colSpan="6" className="p-3 border text-center text-red-700 font-bold">
                ✗ NOT BALANCED: Total Aktiva ≠ Total Pasiva + Laba Rugi
                <br />
                <span className="text-sm">
                  Aktiva: {formatPrice(totalAktiva.closing)} | Pasiva + Laba: {formatPrice(totalPassiva.closing + labaRugiSetelahPajak)} | 
                  Difference: {formatPrice(Math.abs(totalAktiva.closing - (totalPassiva.closing + labaRugiSetelahPajak)))}
                </span>
              </td>
            </tr>
          )}
          <tr><td colSpan="6" className="border-0 p-2"></td></tr>

          {/* GRAND TOTAL - Trial Balance harus Balance! */}
          <tr className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white font-bold text-lg">
            <td colSpan="2" className="p-3 border text-right">TOTAL</td>
            <td className="p-3 border text-right"></td>
            <td className="p-3 border text-right">{formatPrice(totalDebits)}</td>
            <td className="p-3 border text-right">{formatPrice(totalCredits)}</td>
            <td className="p-3 border text-right"></td>
          </tr>
          
          {/* Balance Check Indicator */}
          {isBackendBalanced ? (
            <tr className="bg-green-100">
              <td colSpan="6" className="p-3 border text-center text-green-700 font-bold">
                ✓ Trial Balance: BALANCED (Debit = Credit)
              </td>
            </tr>
          ) : (
            <tr className="bg-red-100">
              <td colSpan="6" className="p-3 border text-center text-red-700 font-bold">
                ✗ Trial Balance: NOT BALANCED 
                <br />
                <span className="text-sm">
                  Difference: {formatPrice(Math.abs(totalDebits - totalCredits))} | 
                  Total Debits: {formatPrice(totalDebits)} | Total Credits: {formatPrice(totalCredits)}
                </span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TrialBalanceTable;
